import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useChatAutoScroll } from '../hooks/useChatAutoScroll';
import {
  useSellerConversations,
  useSellerConversationMessages,
  useSellerLiveInvalidation,
} from '../hooks/queries/sellerQueries';
import { SELLER_CONVERSATION_PARAMS } from '../utils/sellerScope';
import {
  MessageSquare,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  User,
  DollarSign,
} from 'lucide-react';

interface Conversation {
  id?: string;
  _id?: string;
  type?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadBySeller?: number;
  hasActiveOffer?: boolean;
  canSellerReply?: boolean;
  product?: {
    id?: string;
    _id?: string;
    name?: string;
    images?: string[];
  };
  contextProduct?: {
    id?: string;
    _id?: string;
    name?: string;
    images?: string[];
  };
  buyer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface ChatMessage {
  id?: string;
  _id?: string;
  type: string;
  text?: string;
  message?: string;
  createdAt: string;
  offer?: {
    id?: string;
    _id?: string;
    amount?: number;
    bidPrice?: number;
    status?: string;
  };
}

const getId = (item: { id?: string; _id?: string } | string | undefined) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.id || item._id || '';
};

const getMessageText = (msg: ChatMessage) => msg.text || msg.message || '';

const getConversationProduct = (conv: Conversation) => conv.contextProduct || conv.product;

const Conversations: React.FC = () => {
  const { t } = useTranslation('conversations');
  const location = useLocation();
  const routeState = (location.state as { conversationId?: string; productId?: string } | null) || {};
  const initialConversationId = routeState.conversationId || null;
  const initialProductId = routeState.productId || null;
  const { invalidateMessages, invalidateOffers } = useSellerLiveInvalidation();
  const [selectedId, setSelectedId] = useState<string | null>(initialConversationId);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [openingAdminSupport, setOpeningAdminSupport] = useState(false);
  const [actionOfferId, setActionOfferId] = useState<string | null>(null);
  const [counterOfferId, setCounterOfferId] = useState<string | null>(null);
  const [counterAmount, setCounterAmount] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: conversations = [],
    isLoading: loadingList,
  } = useSellerConversations();

  const {
    data: messageList = [],
    isLoading: loadingMessages,
  } = useSellerConversationMessages(selectedId, !!selectedId);

  useEffect(() => {
    if (!selectedId) return undefined;
    void api
      .put(`/conversations/${selectedId}/read`, {}, { params: SELLER_CONVERSATION_PARAMS })
      .catch(() => {});
    return undefined;
  }, [selectedId]);

  const messages = messageList as ChatMessage[];
  const typedConversations = conversations as Conversation[];
  const selectedConversation = typedConversations.find((c) => getId(c) === selectedId);
  const hasAdminSupportThread = typedConversations.some((c) => c.type === 'ADMIN_SELLER');

  useEffect(() => {
    if (selectedId || !initialProductId || typedConversations.length === 0) return;
    const match = typedConversations.find((c) => {
      const product = getConversationProduct(c);
      const pid = product?.id || product?._id;
      return pid && String(pid) === String(initialProductId);
    });
    if (match) setSelectedId(getId(match));
  }, [initialProductId, typedConversations, selectedId]);

  const { scrollToBottom } = useChatAutoScroll({
    containerRef: messagesContainerRef,
    conversationKey: selectedId,
    messageCount: messages.length,
    enabled: !!selectedId && !loadingMessages,
  });

  const handleSend = async () => {
    if (!selectedId || !replyText.trim()) return;
    setSending(true);
    try {
      await api.post(
        `/conversations/${selectedId}/messages`,
        { text: replyText.trim() },
        { params: SELLER_CONVERSATION_PARAMS }
      );
      setReplyText('');
      await invalidateMessages();
      scrollToBottom('smooth');
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; code?: string } };
      };
      const message =
        err.response?.data?.code === 'SELLER_BUYER_REPLY_BLOCKED'
          ? t('messages.buyerMustInitiate')
          : err.response?.data?.message || t('messages.sendFailed');
      alert(message);
    } finally {
      setSending(false);
    }
  };

  const handleOpenAdminSupport = async () => {
    setOpeningAdminSupport(true);
    try {
      const response = await api.post('/seller/messaging/admin', {});
      const conversation = response.data?.data;
      const conversationId = getId(conversation);
      await invalidateMessages();
      if (conversationId) {
        setSelectedId(conversationId);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.adminSupportFailed'));
    } finally {
      setOpeningAdminSupport(false);
    }
  };

  const handleCounterOffer = async (offerId: string) => {
    const amount = parseFloat(counterAmount);
    if (!counterAmount || Number.isNaN(amount) || amount <= 0) {
      alert(t('messages.invalidCounter', { defaultValue: 'Enter a valid counter amount' }));
      return;
    }
    setActionOfferId(offerId);
    try {
      await api.put(`/offers/${offerId}/counter`, { amount });
      setCounterOfferId(null);
      setCounterAmount('');
      await invalidateOffers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.counterFailed'));
    } finally {
      setActionOfferId(null);
    }
  };

  const handleOfferAction = async (offerId: string, action: 'accept' | 'reject') => {
    let reason: string | undefined;
    if (action === 'reject') {
      const promptResult = window.prompt(t('messages.rejectReasonPrompt'));
      if (promptResult === null) return;
      reason = promptResult || undefined;
    }

    setActionOfferId(offerId);
    try {
      if (action === 'accept') {
        await api.put(`/offers/${offerId}/accept`, {});
      } else {
        await api.put(`/offers/${offerId}/reject`, { reason });
      }
      await invalidateOffers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.actionFailed'));
    } finally {
      setActionOfferId(null);
    }
  };

  const renderMessage = (msg: ChatMessage) => {
    const text = getMessageText(msg);
    const offer = msg.offer;
    const offerId = offer ? getId(offer) : '';
    const offerAmount = offer?.amount ?? offer?.bidPrice;
    const isBuyerOfferPending =
      (msg.type === 'OFFER_CREATED' || msg.type === 'BID_PROPOSAL') &&
      offer?.status === 'PENDING' &&
      (offer as { initiatedBy?: string })?.initiatedBy !== 'SELLER';
    const isCounterOffer = msg.type === 'OFFER_COUNTER' || msg.type === 'COUNTER_OFFER';
    const isOfferAccepted = msg.type === 'OFFER_ACCEPTED' || msg.type === 'BID_ACCEPTED';
    const isOfferRejected =
      msg.type === 'OFFER_REJECTED' ||
      msg.type === 'OFFER_CANCELLED' ||
      msg.type === 'OFFER_EXPIRED' ||
      msg.type === 'BID_REJECTED';
    const isTextMessage = msg.type === 'TEXT' || msg.type === 'SYSTEM';

    return (
      <div
        key={getId(msg) || `${msg.createdAt}-${msg.type}`}
        className={`flex ${msg.type === 'PRODUCT_INQUIRY' ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-[85%] rounded-lg px-4 py-3 text-sm ${
            msg.type === 'PRODUCT_INQUIRY'
              ? 'bg-blue-50 text-blue-900'
              : isOfferAccepted
                ? 'bg-green-50 text-green-900 border border-green-200'
                : isOfferRejected
                  ? 'bg-red-50 text-red-900 border border-red-200'
                  : 'bg-gray-100 text-gray-900'
          }`}
        >
          {offerAmount != null && offerAmount > 0 && (
            <p className="font-bold text-lg mb-1">${offerAmount}</p>
          )}
          {isTextMessage && !text && (
            <p className="text-gray-500 italic">{t('messages.emptyText')}</p>
          )}
          {text && <p className="whitespace-pre-wrap">{text}</p>}
          {isTextMessage && text && (
            <p className="text-xs text-gray-400 mt-1">{t('messages.typeText')}</p>
          )}
          <p className="text-xs text-gray-500 mt-2">{new Date(msg.createdAt).toLocaleString()}</p>

          {isBuyerOfferPending && offerId && (
            <div className="flex flex-col gap-2 mt-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={actionOfferId === offerId}
                  onClick={() => handleOfferAction(offerId, 'accept')}
                  className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium flex items-center gap-1"
                >
                  {actionOfferId === offerId && !counterOfferId ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <CheckCircle size={14} />
                  )}
                  {t('actions.accept')}
                </button>
                <button
                  type="button"
                  disabled={actionOfferId === offerId}
                  onClick={() =>
                    counterOfferId === offerId
                      ? setCounterOfferId(null)
                      : setCounterOfferId(offerId)
                  }
                  className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium flex items-center gap-1"
                >
                  <DollarSign size={14} />
                  {t('actions.counter')}
                </button>
                <button
                  type="button"
                  disabled={actionOfferId === offerId}
                  onClick={() => handleOfferAction(offerId, 'reject')}
                  className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {t('actions.reject')}
                </button>
              </div>
              {counterOfferId === offerId && (
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    className="input-field flex-1 text-sm"
                    placeholder={t('messages.counterPlaceholder')}
                    value={counterAmount}
                    onChange={(e) => setCounterAmount(e.target.value)}
                  />
                  <button
                    type="button"
                    disabled={actionOfferId === offerId}
                    onClick={() => handleCounterOffer(offerId)}
                    className="px-3 py-1.5 bg-custom-primary text-white rounded text-xs font-medium"
                  >
                    {t('actions.sendCounter')}
                  </button>
                </div>
              )}
            </div>
          )}
          {isCounterOffer && offerAmount != null && (
            <p className="text-xs text-amber-700 mt-2">{t('messages.counterSent')}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-x-hidden h-[calc(100vh-8rem)] flex flex-col">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-sm md:text-base text-gray-600 mt-1">{t('subtitle')}</p>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-0">
        <div
          className={`card overflow-hidden flex flex-col min-h-0 ${
            selectedId ? 'hidden lg:flex' : 'flex'
          }`}
        >
          <div className="p-3 border-b border-gray-100">
            <button
              type="button"
              onClick={handleOpenAdminSupport}
              disabled={openingAdminSupport}
              className="w-full px-3 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
            >
              {openingAdminSupport
                ? t('messages.openingAdminSupport')
                : hasAdminSupportThread
                  ? t('actions.openAdminSupport')
                  : t('actions.contactAdminSupport')}
            </button>
          </div>
          {loadingList && typedConversations.length === 0 ? (
            <div className="flex items-center justify-center flex-1 p-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-primary" />
            </div>
          ) : typedConversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare className="mx-auto mb-3 text-gray-400" size={40} />
              {t('messages.noConversations')}
            </div>
          ) : (
            <ul className="overflow-y-auto flex-1 divide-y divide-gray-100">
              {typedConversations.map((conv) => {
                const convId = getId(conv);
                const unread = (conv.unreadBySeller || 0) > 0;
                const product = getConversationProduct(conv);
                const buyer = conv.buyer;
                const isAdminSupport = conv.type === 'ADMIN_SELLER';
                const previewProduct = isAdminSupport ? null : product;
                const showOfferBadge = !isAdminSupport && conv.hasActiveOffer;
                const buyerName = isAdminSupport
                  ? t('fields.platformSupport', { defaultValue: 'VOG Support' })
                  : buyer
                    ? `${buyer.firstName || ''} ${buyer.lastName || ''}`.trim() || buyer.email
                    : t('fields.customer');

                return (
                  <li key={convId}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(convId)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                        selectedId === convId ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {previewProduct?.images?.[0] ? (
                          <img
                            src={previewProduct.images[0]}
                            alt=""
                            className="w-12 h-12 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center">
                            <User size={20} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between gap-2">
                            <p className="font-medium text-gray-900 truncate">{buyerName}</p>
                            {unread && (
                              <span className="bg-custom-primary text-white text-xs px-2 py-0.5 rounded-full">
                                {conv.unreadBySeller}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate flex items-center gap-2">
                            <span className="truncate">
                              {isAdminSupport
                                ? t('messages.adminThread', { defaultValue: 'Platform support' })
                                : product?.name}
                            </span>
                            {showOfferBadge && (
                              <span className="flex-shrink-0 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                {t('messages.activeOffer')}
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-600 truncate mt-1">
                            {conv.lastMessage || t('messages.noPreview')}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className={`lg:col-span-2 card flex flex-col min-h-0 ${
            selectedId ? 'flex' : 'hidden lg:flex'
          }`}
        >
          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
              <MessageSquare size={48} className="text-gray-300 mb-3" />
              <p>{t('messages.selectConversation')}</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                <button
                  type="button"
                  className="lg:hidden p-2 -ml-2"
                  onClick={() => setSelectedId(null)}
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {selectedConversation?.type === 'ADMIN_SELLER'
                      ? t('fields.platformSupport')
                      : selectedConversation?.buyer
                        ? `${selectedConversation.buyer.firstName || ''} ${selectedConversation.buyer.lastName || ''}`.trim() ||
                          selectedConversation.buyer.email
                        : getConversationProduct(selectedConversation || {})?.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {selectedConversation?.type === 'ADMIN_SELLER'
                      ? t('messages.adminThread')
                      : getConversationProduct(selectedConversation || {})?.name ||
                        selectedConversation?.buyer?.email}
                  </p>
                </div>
                <Link
                  to="/offers"
                  className="text-sm text-custom-primary hover:underline whitespace-nowrap"
                >
                  {t('actions.allOffers')}
                </Link>
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-3"
                style={{ overflowAnchor: 'none' }}
              >
                {loadingMessages && messages.length === 0 ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="animate-spin text-custom-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{t('messages.noMessages')}</p>
                ) : (
                  messages.map(renderMessage)
                )}
              </div>

              <div className="p-4 border-t border-gray-200 flex flex-col gap-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    placeholder={
                      selectedConversation?.type === 'ADMIN_SELLER'
                        ? t('messages.adminReplyPlaceholder')
                        : t('messages.replyPlaceholder')
                    }
                    className="input-field flex-1"
                    disabled={!selectedId}
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={sending || !replyText.trim() || !selectedId}
                    className="px-4 py-2 bg-custom-primary text-white rounded-lg disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conversations;
